import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import TikTokEmbedModal from './TikTokEmbedModal';

interface PlayableVideoThumbnailProps {
  /** Video cover image URL */
  coverImageUrl?: string;
  /** Video title for alt text */
  title: string;
  /** TikTok video ID for embed playback */
  tiktokId?: string;
  /** TikTok username for profile fallback */
  username?: string;
  /** Internal video ID for navigation fallback */
  videoId?: string;
  /** Additional CSS classes for sizing/styling */
  className?: string;
  /** Stop click propagation (useful when nested in other clickable elements) */
  stopPropagation?: boolean;
  /** Callback fired when thumbnail is clicked */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Reusable video thumbnail with play button overlay.
 * Clicking opens TikTok embed modal (if tiktokId provided) or navigates to video detail.
 * Videos autoplay and are unmuted when opened.
 */
export default function PlayableVideoThumbnail({
  coverImageUrl,
  title,
  tiktokId,
  username,
  videoId,
  className = "w-20 sm:w-24 h-28 sm:h-32",
  stopPropagation = false,
  onClick
}: PlayableVideoThumbnailProps) {
  const [showEmbed, setShowEmbed] = useState(false);
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    // Call custom onClick if provided
    if (onClick) {
      onClick(e);
    }

    // Default behavior: open embed modal or navigate
    if (tiktokId) {
      setShowEmbed(true);
    } else if (username && videoId) {
      navigate(`/dashboard/${username}/video/${videoId}`);
    } else if (username) {
      window.open(`https://www.tiktok.com/@${username}`, '_blank');
    }
  };

  const placeholderUrl = `https://placehold.co/100x150/0f172a/475569?text=No+Preview`;

  return (
    <>
      <button
        className={`bg-slate-800 rounded-lg flex-shrink-0 relative overflow-hidden group border border-slate-700 hover:border-primary-500 transition-colors ${className}`}
        onClick={handleClick}
        type="button"
      >
        <img
          src={coverImageUrl || placeholderUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
          referrerPolicy="no-referrer"
          title="TikTok image links expire periodically. Sync your account to refresh previews."
          onError={(e) => {
            (e.target as HTMLImageElement).src = `https://placehold.co/100x150/0f172a/475569?text=Expired`;
          }}
        />
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
          </div>
        </div>
      </button>

      {/* TikTok Embed Modal */}
      {showEmbed && tiktokId && (
        <TikTokEmbedModal
          tiktokId={tiktokId}
          username={username}
          onClose={() => setShowEmbed(false)}
        />
      )}
    </>
  );
}
